import { createTheme, type Theme, type ThemeOptions } from '@mui/material/styles';
import { brand } from './brand';

/**
 * Light-only product theme. Dark mode is intentionally unsupported —
 * the gallery language is a white museum wall.
 */
function baseOptions(): ThemeOptions {
  const {
    ink,
    paper,
    whisper,
    carbon,
    graphite,
    ash,
    fog,
    mist,
    danger,
    warning,
    info,
  } = brand.palette;

  return {
    palette: {
      mode: 'light',
      primary: { main: carbon, contrastText: paper },
      secondary: { main: graphite, contrastText: paper },
      error: { main: danger },
      warning: { main: warning },
      info: { main: info },
      success: { main: '#2F6B4F', contrastText: paper },
      background: { default: paper, paper },
      text: { primary: ink, secondary: ash, disabled: fog },
      divider: whisper,
      action: {
        active: ink,
        hover: 'rgba(32, 32, 32, 0.06)',
        selected: 'rgba(32, 32, 32, 0.1)',
        disabled: fog,
        disabledBackground: whisper,
      },
    },
    shape: { borderRadius: brand.radius.cards },
    typography: {
      fontFamily: brand.fontFamily,
      allVariants: {
        letterSpacing: '0.02em',
      },
      h1: {
        fontFamily: brand.displayFamily,
        fontSize: '38px',
        fontWeight: 500,
        letterSpacing: '0.02em',
        lineHeight: 1.11,
        color: ink,
      },
      h2: {
        fontFamily: brand.displayFamily,
        fontSize: '18px',
        fontWeight: 500,
        letterSpacing: '0.02em',
        lineHeight: 1.4,
        color: ink,
      },
      h3: {
        fontFamily: brand.displayFamily,
        fontSize: '16px',
        fontWeight: 500,
        letterSpacing: '0.02em',
        lineHeight: 1.4,
        color: ink,
      },
      h4: {
        fontSize: '14px',
        fontWeight: 500,
        letterSpacing: '0.02em',
        lineHeight: 1.43,
      },
      h5: {
        fontSize: '13px',
        fontWeight: 500,
        letterSpacing: '0.02em',
        lineHeight: 1.4,
      },
      h6: {
        fontSize: '12px',
        fontWeight: 500,
        letterSpacing: '0.02em',
        lineHeight: 1.4,
      },
      subtitle1: {
        fontSize: '16px',
        fontWeight: 400,
        letterSpacing: '0.02em',
        lineHeight: 1.6,
        color: ash,
      },
      subtitle2: {
        fontSize: '13px',
        fontWeight: 500,
        letterSpacing: '0.02em',
        lineHeight: 1.4,
      },
      body1: {
        fontSize: '14px',
        fontWeight: 400,
        letterSpacing: '0.02em',
        lineHeight: 1.43,
      },
      body2: {
        fontSize: '13px',
        fontWeight: 400,
        letterSpacing: '0.02em',
        lineHeight: 1.43,
      },
      caption: {
        fontSize: '12px',
        fontWeight: 400,
        letterSpacing: '0.02em',
        lineHeight: 1.35,
      },
      button: {
        textTransform: 'none',
        fontWeight: 500,
        letterSpacing: '0.02em',
        fontSize: '13px',
      },
      overline: {
        fontSize: '11px',
        fontWeight: 500,
        letterSpacing: '0.02em',
        textTransform: 'none',
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            overscrollBehaviorY: 'none',
            backgroundColor: paper,
            color: ink,
          },
          '*::-webkit-scrollbar': { width: 8, height: 8 },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: fog,
            borderRadius: brand.radius.pills,
            border: '2px solid transparent',
            backgroundClip: 'content-box',
          },
          '*::-webkit-scrollbar-thumb:hover': { backgroundColor: graphite },
          '*::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: brand.radius.pills,
            paddingInline: 18,
            paddingBlock: 10,
            fontWeight: 500,
            fontSize: 13,
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          },
          sizeSmall: {
            paddingInline: 14,
            paddingBlock: 6,
            fontSize: 12,
          },
          sizeLarge: {
            paddingInline: 22,
            paddingBlock: 12,
            fontSize: 14,
          },
          contained: {
            backgroundColor: carbon,
            color: paper,
            '&:hover': { backgroundColor: brand.palette.slate },
            '&.Mui-disabled': {
              backgroundColor: whisper,
              color: graphite,
            },
          },
          outlined: {
            borderColor: graphite,
            color: graphite,
            backgroundColor: 'transparent',
            borderWidth: 1,
            '&:hover': {
              borderColor: ink,
              color: ink,
              backgroundColor: 'transparent',
              borderWidth: 1,
            },
          },
          text: {
            color: ink,
            '&:hover': { backgroundColor: 'rgba(32, 32, 32, 0.04)' },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: brand.radius.pills,
            color: ink,
            transition: 'background-color 120ms ease, color 120ms ease',
            '&:hover': {
              backgroundColor: 'rgba(32, 32, 32, 0.06)',
            },
          },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: paper,
          },
          outlined: {
            borderColor: whisper,
          },
        },
      },
      MuiCard: {
        defaultProps: { variant: 'outlined', elevation: 0 },
        styleOverrides: {
          root: {
            borderRadius: brand.radius.cards,
            borderColor: whisper,
            boxShadow: 'none',
            backgroundImage: 'none',
          },
        },
      },
      MuiTextField: { defaultProps: { size: 'small' } },
      MuiSelect: { defaultProps: { size: 'small' } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: brand.radius.cards,
            backgroundColor: paper,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: whisper,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: fog,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: carbon,
              borderWidth: 1,
            },
          },
          input: {
            fontSize: 14,
            letterSpacing: '0.02em',
          },
          multiline: {
            borderRadius: brand.radius.cards,
            padding: 0,
          },
        },
      },
      // Editorial meta labels — no pill chrome. Color carries status; selected =
      // weight, not a filled badge.
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            fontWeight: 500,
            fontSize: 11,
            letterSpacing: '0.02em',
            height: 'auto',
            minHeight: 0,
            backgroundColor: 'transparent',
            border: 'none',
            transition: 'opacity 120ms ease, color 120ms ease',
            '&.MuiChip-clickable:hover, &.MuiChip-clickable:focus': {
              backgroundColor: 'transparent',
            },
            '&.MuiChip-clickable:hover': { opacity: 0.72 },
            '&.Mui-disabled': {
              opacity: 0.4,
              backgroundColor: 'transparent',
            },
          },
          sizeSmall: { height: 'auto', fontSize: 11 },
          sizeMedium: { height: 'auto', fontSize: 12 },
          label: { padding: 0 },
          icon: { marginLeft: 0, marginRight: 4, fontSize: 14 },
          deleteIcon: {
            margin: 0,
            marginLeft: 4,
            fontSize: 14,
            color: 'inherit',
            opacity: 0.55,
            '&:hover': { opacity: 1, color: 'inherit' },
          },
          outlined: {
            border: 'none',
            backgroundColor: 'transparent',
            color: graphite,
          },
          filled: {
            border: 'none',
            backgroundColor: 'transparent',
          },
          colorDefault: {
            '&.MuiChip-filled, &.MuiChip-outlined': {
              backgroundColor: 'transparent',
              border: 'none',
              color: graphite,
            },
            '&.MuiChip-filled': { color: carbon },
          },
          colorPrimary: {
            '&.MuiChip-filled, &.MuiChip-outlined': {
              backgroundColor: 'transparent',
              border: 'none',
              color: graphite,
            },
            '&.MuiChip-filled': { color: carbon, fontWeight: 600 },
          },
          colorSecondary: {
            '&.MuiChip-filled, &.MuiChip-outlined': {
              backgroundColor: 'transparent',
              border: 'none',
              color: graphite,
            },
            '&.MuiChip-filled': { color: graphite, fontWeight: 600 },
          },
          colorSuccess: {
            '&.MuiChip-filled, &.MuiChip-outlined': {
              backgroundColor: 'transparent',
              border: 'none',
              color: '#2F6B4F',
            },
          },
          colorWarning: {
            '&.MuiChip-filled, &.MuiChip-outlined': {
              backgroundColor: 'transparent',
              border: 'none',
              color: warning,
            },
          },
          colorError: {
            '&.MuiChip-filled, &.MuiChip-outlined': {
              backgroundColor: 'transparent',
              border: 'none',
              color: danger,
            },
          },
          colorInfo: {
            '&.MuiChip-filled, &.MuiChip-outlined': {
              backgroundColor: 'transparent',
              border: 'none',
              color: info,
            },
          },
        },
      },
      MuiTooltip: {
        defaultProps: { arrow: true, enterDelay: 400 },
        styleOverrides: {
          tooltip: {
            fontSize: 11,
            borderRadius: brand.radius.pills,
            paddingBlock: 6,
            paddingInline: 10,
            backgroundColor: carbon,
            letterSpacing: '0.02em',
          },
          arrow: { color: carbon },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: brand.radius.cards,
            border: `1px solid ${whisper}`,
            boxShadow: 'none',
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: brand.radius.pills,
            letterSpacing: '0.02em',
            transition: 'background-color 120ms ease, color 120ms ease',
            '&:hover': {
              backgroundColor: 'rgba(32, 32, 32, 0.06)',
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            fontSize: 13,
            letterSpacing: '0.02em',
            minHeight: 36,
            borderRadius: brand.radius.pills,
            color: ink,
            '&.Mui-selected': {
              color: paper,
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: { display: 'none' },
          list: { gap: 4 },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            fontSize: 12,
            letterSpacing: '0.02em',
            borderRadius: brand.radius.pills,
            borderColor: whisper,
            color: graphite,
            paddingInline: 14,
            paddingBlock: 6,
            '&.Mui-selected': {
              backgroundColor: carbon,
              color: paper,
              borderColor: carbon,
              '&:hover': {
                backgroundColor: brand.palette.slate,
              },
            },
            '&:hover': {
              backgroundColor: 'rgba(32, 32, 32, 0.04)',
              borderColor: fog,
            },
          },
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: {
            gap: 4,
          },
          grouped: {
            borderRadius: `${brand.radius.pills}px !important`,
            border: `1px solid ${whisper} !important`,
            marginLeft: '0 !important',
          },
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0, color: 'transparent' },
        styleOverrides: {
          root: {
            backgroundColor: paper,
            color: ink,
            borderBottom: 'none',
            boxShadow: 'none',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: paper,
            borderColor: whisper,
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: { borderColor: whisper },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: brand.radius.cards,
            letterSpacing: '0.02em',
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            backgroundColor: carbon,
            color: paper,
            fontWeight: 500,
            letterSpacing: '0.02em',
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            color: ink,
            textDecorationColor: mist,
            '&:hover': { textDecorationColor: ink },
          },
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: {
            backgroundColor: whisper,
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          switchBase: {
            color: paper,
            '&.Mui-checked': {
              color: carbon,
              '& + .MuiSwitch-track': {
                backgroundColor: carbon,
                opacity: 1,
              },
            },
            '&.Mui-disabled': {
              color: mist,
              '& + .MuiSwitch-track': {
                opacity: 0.5,
              },
            },
          },
          track: {
            backgroundColor: fog,
            borderRadius: brand.radius.pills,
            opacity: 1,
          },
          thumb: {
            boxShadow: 'none',
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: fog,
            '&.Mui-checked': { color: carbon },
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            color: fog,
            '&.Mui-checked': { color: carbon },
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: brand.radius.pills,
            backgroundColor: whisper,
            height: 4,
          },
          bar: {
            borderRadius: brand.radius.pills,
            backgroundColor: carbon,
          },
        },
      },
      MuiFab: {
        styleOverrides: {
          root: {
            borderRadius: brand.radius.pills,
            boxShadow: brand.shadow.sm,
            backgroundColor: carbon,
            color: paper,
            '&:hover': {
              backgroundColor: brand.palette.slate,
              boxShadow: brand.shadow.sm,
            },
          },
        },
      },
    },
  };
}

export function createAppTheme(): Theme {
  return createTheme(baseOptions());
}
